import React, { useMemo, useState, useEffect, useRef } from 'react'
import MultiChoiceOption from "components/challenges/multiChoice/MultiChoiceOption"
import PopupTooltip from "components/PopupTooltip"
import ChallengeTag from "components/ChallengeTag"

import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { themes } from "prism-react-renderer";

import * as lessonParser from "utils/lessonParser"

import AIExplanation, { AIExplainIcon } from "components/AIExplanation"

const theme = themes.dracula;
const customStyle = {
    width: "fit-content",
    borderRadius: '0',
    backgroundColor: 'transparent',
    padding: '0',
    margin: '0',
    fontSize: '20px'
};


function shuffleArray(array) {
    const shuffled = array.slice();
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
}

function MultiChoiceSplit({ questionData, questionText, questionStatus, onAnswerCheck, questionVariables, onHelpUsed, tag }) {
    const aiRef = useRef();
    const lessonScrollRef = useRef(null);
    const [selected, setSelected] = useState(null);
    const popupRef = useRef(null);

    const choices = useMemo(() => {
        if (!questionData?.data) return [];

        const correctChoices = lessonParser
            .parseValueList(questionData.data.choices?.correct, false, questionVariables)
            .map(choice => ({ ...choice, answer: true }));

        const incorrectChoices = lessonParser
            .parseValueList(
                questionData.data.choices?.incorrect,
                false,
                questionVariables,
                correctChoices[0]?.value
            )
            .map(choice => ({ ...choice, answer: false }));

        const allChoices = [...correctChoices, ...incorrectChoices];
        // console.log("Choices:", allChoices)
        return shuffleArray(allChoices);
    }, [JSON.stringify(questionData?.data?.choices), questionVariables]);

    useEffect(() => {
        setSelected(null);
    }, [questionData?.data?.code]);

    const parsedCode = useMemo(() => {
        return lessonParser.replaceVariablesInText(
            questionData.data?.code || "",
            questionVariables
        );
    }, [questionData.data?.code, questionVariables]);

    const handleHelpClick = () => {
        // Hide the popup by marking it as seen
        if (popupRef.current) {
            popupRef.current.handleInteraction();
        }

        // Notify parent that help was used
        onHelpUsed?.();

        // Trigger AI explanation
        aiRef.current?.getAIExplanation(questionData, {
            code: parsedCode,
            question: questionText,
            choices: choices,
            selected: selected
        });
        aiRef.current?.showSection();
        setTimeout(() => {
            if (lessonScrollRef.current) {
                lessonScrollRef.current.scrollTo({
                    top: lessonScrollRef.current.scrollHeight,
                    behavior: 'smooth'
                });
            }
        }, 200);
    };

    useEffect(() => {
        onAnswerCheck((force) => {
            if (selected === null && !force) return null

            setTimeout(() => {
                if (lessonScrollRef.current) {
                    lessonScrollRef.current.scrollTo({
                        top: lessonScrollRef.current.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            }, 10);
            let isCorrect = !!choices[selected]?.answer;
            if (!isCorrect && !force) {
                requestAnimationFrame(() => {
                    aiRef.current?.getAIExplanation(questionData, {
                        code: parsedCode,
                        question: questionText,
                        choices: choices,
                        selected: selected
                    });
                });
            }
            return { isCorrect: isCorrect, message: choices.find(c => c.answer)?.value };
        });
    }, [onAnswerCheck, selected, questionData]);

    return (
        // <div className=' flex flex-row w-full gap-12  p-4'>
        //     <div className='flex items-center justify-center w-full grow-1 '>
        //         <div className='mb-4 flex items-center justify-center w-full h-fit border-border border-[3px]  rounded-xl bg-background-light p-2'>
        //             <div className='text-center text-2xl font-bold  w-full bg-code p-3 px-2 rounded-xl'>
        //                 <SyntaxHighlighter language="python" style={theme} customStyle={customStyle} codeTagProps={[]} showLineNumbers={true} wrapLongLines >
        //                     {"print(input())\n\nfor i in range(10):\n    print(i)"}
        //                 </SyntaxHighlighter>
        //             </div>
        //         </div>
        //     </div>
        //     <div className=' flex items-center flex-col w-full justify-center'>
        //         <h1 className='px-10 break-small:px-0 w-full font-bold text-center break-small:text-left text-[4vw] break-small:text-3xl mb-[4vh] break-big:mb-[5vh]'>{questionText}</h1>
        //         <div className="w-full">
        //             <div className='Choices w-full grid gap-3 grid-cols-1 place-items-center pb-8'>
        //                 {choices.map((choice, optionIndex) =>
        //                     <MultiChoiceOption text={choice.data} optionIndex={optionIndex} questionStatus={questionStatus} selected={selected} setSelected={setSelected} key={choice.data} />
        //                 )}
        //             </div>
        //         </div>
        //     </div>
        // </div>
        <div ref={lessonScrollRef} className='text-text flex justify-center w-full overflow-auto max-h-full py-2'>
            <div className=' grid grid-cols-2 auto-rows-min w-full gap-5 break-big:gap-10 gap-y-6 p-4 max-w-[1100px]'>
                <div className="h-min pt-1 flex justify-end items-center">
                    {tag && <ChallengeTag tag={tag} />}
                </div>
                <h1 className='h-min px-10 break-small:px-0 w-full col-span-2 break-small:col-span-1 font-bold text-center break-small:text-left text-[4vw] break-small:text-3xl '>{questionText}</h1>

                <div className='h-min flex items-center justify-center w-full '>
                    <div className='mb-4 flex items-center justify-center w-full h-fit border-border border-[3px]  rounded-xl bg-background-light p-2'>
                        <div className='text-white text-center text-2xl font-bold  w-full bg-code p-3 px-4 pr-2 rounded-[.5rem]'>
                            <SyntaxHighlighter language="python" style={theme} customStyle={customStyle} codeTagProps={[]} showLineNumbers={true} wrapLongLines >
                                {parsedCode}
                            </SyntaxHighlighter>
                        </div>
                    </div>
                </div>
                <div className='h-min flex items-center flex-col w-full justify-center'>
                    <div className="w-full">
                        <div className='Choices w-full grid gap-3 grid-cols-1 place-items-center pb-8'>
                            {choices.map((choice, optionIndex) =>
                                <MultiChoiceOption key={choice.value} text={choice.value} optionIndex={optionIndex} questionStatus={questionStatus} selected={selected} setSelected={setSelected} options={questionData.data?.options} />
                            )}
                        </div>
                    </div>
                    {(questionStatus["guessed"] && !questionStatus["answer"]) && <div className='w-full min-h-40 rounded-xl bg-warning-darker border-warning border-[3px] flex p-6 px-6  flex-col gap-5 mb-8' style={{ animation: "incorrectSection 450ms ease" }}>
                        <div className='flex flex-col sm:flex-row items-center justify-between'>
                            <h1 className='text-text text-xl font-bold text-left'>Correct Answer:</h1>
                            <button
                                className='pr-1 text-primary text-base font-bold text-right flex items-center gap-2 outline-none hover:underline relative'
                                onClick={handleHelpClick}
                            ><AIExplainIcon /> See how to get this answer
                                <PopupTooltip
                                    ref={popupRef}
                                    text="Stuck? Use free AI to help you understand it better!"
                                    open={questionStatus["guessed"] && !questionStatus["answer"]}
                                    storageKey="seenHelpPopup"
                                    autoMarkSeenAfter={2000}
                                />
                            </button>
                        </div>

                        <MultiChoiceOption text={choices.find(c => c.answer)?.value} optionIndex={0} questionStatus={{ "guessed": true }} selected={1} setSelected={setSelected} key={"Incorrect Display " + choices.find(c => c.answer)?.value} options={questionData.data?.options} />
                        <AIExplanation ref={aiRef} />
                    </div>}
                </div>
            </div>
        </div>
    )
}


export default MultiChoiceSplit